import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../api/apiClient';
import { useApiQuery } from '../../../../../../api/hooks';
import { useConfirmationModalStore, usePreloaderStore } from '../../../../../../stores';
import { Endpoints, QueryKey, Defaults, SanctionEffect } from '../../../../../../views/democracy/constants';
import { valueToMediaRefs, buildMultipartRequest, extractErrorMessage } from '../../../../../../views/democracy/utils';
import AppMedia from '../../../controls/media/AppMedia';
import Modal from '../../../controls/Modal';
import LabeledInput from '../../../controls/LabeledInput';
import LabeledSelect from '../../../controls/LabeledSelect';

// Модалка редактирования санкции (стоимость/эффект/медиа-фон).
export default function SanctionEditModal({ sanction, onChanged, onClose }) {
    const { enqueueSnackbar } = useSnackbar();
    const { open: openConfirmation } = useConfirmationModalStore();
    const { show, hide } = usePreloaderStore();

    const { data: effects = [] } = useApiQuery(QueryKey.sanctionEffects, Endpoints.sanctions.effects, { staleTime: Infinity });

    const [data, setData] = useState({
        cost: sanction.cost ?? 10,
        title: sanction.title || '',
        description: sanction.description || '',
        media: sanction.media || null,
        effect: sanction.effect || SanctionEffect.NONE,
        maxConcurrent: sanction.maxConcurrent ?? Defaults.sanctionMaxConcurrent
    });
    const [mediaPendingFile, setMediaPendingFile] = useState(null);

    const setField = (field, value) => setData(prev => ({ ...prev, [field]: value }));
    const effectOptions = effects.map(e => ({ key: e.name, value: e.displayName || e.name }));

    const handleSave = async () => {
        show();
        try {
            const payload = {
                sanctionType: sanction.sanctionType,
                effect: data.effect,
                cost: parseInt(data.cost, 10) || 0,
                title: data.title,
                description: data.description || null,
                maxConcurrent: parseInt(data.maxConcurrent, 10) || Defaults.sanctionMaxConcurrent,
                media: valueToMediaRefs(data.media ? [data.media] : [])
            };
            const formData = buildMultipartRequest(payload, mediaPendingFile ? [mediaPendingFile] : []);
            await apiClient.put(Endpoints.sanctions.update(sanction.id), formData);
            enqueueSnackbar('Сохранено', { variant: 'success' });
            onChanged();
            onClose();
        } catch (error) {
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось сохранить санкцию'), { variant: 'error' });
        } finally {
            hide();
        }
    };

    const handleDelete = () => {
        openConfirmation(`Удалить санкцию «${sanction.title}»?`, async () => {
            show();
            try {
                await apiClient.delete(Endpoints.sanctions.delete(sanction.id));
                enqueueSnackbar('Удалено', { variant: 'success' });
                onChanged();
                onClose();
            } catch (error) {
                enqueueSnackbar(extractErrorMessage(error, 'Не удалось удалить'), { variant: 'error' });
            } finally {
                hide();
            }
        });
    };

    const footer = (
        <>
            <button type="button" className="btn btn-danger" onClick={handleDelete}>Удалить</button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>Сохранить</button>
        </>
    );

    return (
        <Modal open title="Редактирование санкции" onClose={onClose} size="lg" footer={footer}>
            <div className="row-3">
                <LabeledInput label="Стоимость" value={String(data.cost)} onChange={(e) => setField('cost', e.target.value)} />
                <LabeledInput label="Макс. одновременно" value={String(data.maxConcurrent)} onChange={(e) => setField('maxConcurrent', e.target.value)} />
                <LabeledSelect label="Эффект" options={effectOptions} value={data.effect} onChange={(e) => setField('effect', e.target.value)} />
            </div>
            <LabeledInput label="Название" value={data.title} onChange={(e) => setField('title', e.target.value)} />
            <div>
                <label className="ctl-label">Описание</label>
                <textarea className="textarea" value={data.description} onChange={(e) => setField('description', e.target.value)} />
            </div>
            <div>
                <label className="ctl-label">Медиа (фон карточки)</label>
                <AppMedia
                    value={data.media}
                    pendingFile={mediaPendingFile}
                    onChange={(nextValue, nextPending) => { setField('media', nextValue); setMediaPendingFile(nextPending); }}
                    type="IMAGE"
                />
            </div>
        </Modal>
    );
}

SanctionEditModal.propTypes = {
    sanction: PropTypes.shape({
        id: PropTypes.number.isRequired,
        sanctionType: PropTypes.string,
        cost: PropTypes.number,
        title: PropTypes.string,
        description: PropTypes.string,
        media: PropTypes.object,
        effect: PropTypes.string,
        maxConcurrent: PropTypes.number
    }).isRequired,
    onChanged: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};
